import React from "react";
import { RotateCcw } from "lucide-react";
import { Radar } from "react-chartjs-2";

import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

import "./PrivateTalentPulseResult.css";

ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

const OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: 4,
  },
  scales: {
    r: {
      min: 0,
      max: 100,
      angleLines: {
        color: "rgba(255,255,255,0.1)",
      },
      grid: {
        color: "rgba(255,255,255,0.1)",
      },
      pointLabels: {
        color: "#AAAEB3",
        font: {
          size: 10,
          weight: "600",
        },
      },
      ticks: {
        display: false,
      },
    },
  },
  plugins: {
    legend: {
      display: false,
    },
  },
};

const COMPACT_OPTIONS = {
  ...OPTIONS,
  layout: {
    padding: 2,
  },
  scales: {
    r: {
      ...OPTIONS.scales.r,
      pointLabels: {
        display: false,
      },
      grid: {
        color: "rgba(255,255,255,0.08)",
      },
    },
  },
};

const getRadarScores = (pulse) => {
  if (Array.isArray(pulse?.radarStats)) {
    return pulse.radarStats.map(Number);
  }

  return [
    pulse?.pace,
    pulse?.agility,
    pulse?.vision,
    pulse?.power,
    pulse?.technique,
  ].map((value) =>
    value === null || value === undefined || value === "" ? NaN : Number(value),
  );
};

const getMetricLabels = (pulse, metrics) => {
  if (Array.isArray(metrics) && metrics.length === 5) {
    return metrics;
  }

  if (Array.isArray(pulse?.metricLabels) && pulse.metricLabels.length === 5) {
    return pulse.metricLabels;
  }

  return [];
};

export default function PrivateTalentPulseResult({
  pulse,
  metrics = [],
  onRetake,
  compact = false,
  readOnly = false,
}) {
  if (!pulse) return null;

  const scores = getRadarScores(pulse);
  const labels = getMetricLabels(pulse, metrics);

  const canShowRadar =
    scores.length === 5 &&
    labels.length === 5 &&
    scores.every(
      (value) => Number.isFinite(value) && value >= 0 && value <= 100,
    );

  const strengths = Array.isArray(pulse.strengths)
    ? pulse.strengths
    : typeof pulse.strengths === "string"
      ? pulse.strengths
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      : [];

  const chartData = {
    labels,
    datasets: [
      {
        label: pulse.title || "Talent Pulse",
        data: scores,
        backgroundColor: "rgba(0,123,255,0.2)",
        borderColor: "#007bff",
        borderWidth: 2,
        pointBackgroundColor: "#007bff",
        pointRadius: compact ? 0 : 2,
        fill: true,
      },
    ],
  };

  const assessmentDate = pulse.createdAt ? new Date(pulse.createdAt) : null;

  const date =
    assessmentDate && !Number.isNaN(assessmentDate.getTime())
      ? assessmentDate.toLocaleDateString("en-AU", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })
      : null;

  return (
    <section
      className={[
        "private-talent-pulse-result",
        compact && "private-talent-pulse-result--compact",
        readOnly && "private-talent-pulse-result--readonly",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="private-talent-pulse-result__header">
        <div className="private-talent-pulse-result__heading">
          <span className="private-talent-pulse-result__eyebrow">
            {pulse.sport ? `${pulse.sport} · ` : ""}
            KATCH TALENT PULSE
          </span>

          <h2>{pulse.title || "Talent Pulse"}</h2>

          {pulse.comparison && (
            <p className="private-talent-pulse-result__comparison">
              {pulse.comparison}
            </p>
          )}
        </div>

        {!readOnly && typeof onRetake === "function" && (
          <button
            type="button"
            className="private-talent-pulse-result__retake"
            onClick={onRetake}
          >
            <RotateCcw size={15} />
            <span>Retake Test</span>
          </button>
        )}
      </div>

      <div className="private-talent-pulse-result__layout">
        <div className="private-talent-pulse-result__chart">
          {canShowRadar ? (
            <>
              <div className="private-talent-pulse-result__radar-full">
                <Radar data={chartData} options={OPTIONS} />
              </div>

              <div className="private-talent-pulse-result__radar-compact">
                <Radar data={chartData} options={COMPACT_OPTIONS} />
              </div>
            </>
          ) : (
            <p className="private-talent-pulse-result__disclaimer">
              Radar chart data is unavailable.
            </p>
          )}
        </div>

        <div className="private-talent-pulse-result__details">
          <h3>Playing Style Strengths</h3>

          <div className="private-talent-pulse-result__strengths">
            {strengths.map((strength, index) => (
              <span key={`${strength}-${index}`}>{strength}</span>
            ))}
          </div>

          {pulse.note && (
            <p className="private-talent-pulse-result__note">{pulse.note}</p>
          )}

          {date && (
            <small className="private-talent-pulse-result__date">
              Last assessment: {date}
            </small>
          )}
        </div>
      </div>

      <p className="private-talent-pulse-result__disclaimer private-talent-pulse-result__footer">
        This self-reported assessment describes a playing-style archetype, not
        measured athletic performance.
      </p>
    </section>
  );
}
